import { useEffect, useState, Suspense, lazy, useRef } from "react";
import type { User } from "@supabase/supabase-js";
import { Helmet } from "react-helmet-async";
import { supabase } from "./lib/supabase";
import { getEffectivePlan } from "./lib/planConfig";
import Navbar from "./components/Navbar";

const Landing = lazy(() => import("./pages/Landing"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Campaign = lazy(() => import("./pages/Campaign"));
const Analytics = lazy(() => import("./pages/Analytics"));
const JobPortal = lazy(() => import("./pages/JobPortal"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Refund = lazy(() => import("./pages/Refund"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const ContentView = lazy(() => import("./pages/ContentView"));
const EmailVerifier = lazy(() => import("./pages/EmailVerifier"));
const GmailCallback = lazy(() => import("./pages/GmailCallback"));
// AUTO APPLY HIDDEN — uncomment to re-enable
// const AutoApply = lazy(() => import("./pages/AutoApply"));
import AuthModal from "./components/AuthModal";
import { blogPages, alternativesPages, SEOPage } from "./content/seoPages";

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [seoPage, setSeoPage] = useState<SEOPage | null>(null);
  const [view, setView] = useState<"landing" | "dashboard" | "campaign" | "analytics" | "jobs" | "auto_apply" | "privacy" | "terms" | "about" | "refund" | "contact" | "pricing_page" | "content" | "admin_verify" | "gmail_callback">(() => {
    // Check if we have a jobId in the URL, force Jobs view
    if (new URLSearchParams(window.location.search).get('jobId')) {
      return "jobs";
    }

    // Check real URL for SEO pages (e.g. /blog/..., /alternatives/...)
    const path = window.location.pathname;
    if (path.startsWith('/blog/')) {
      const slug = path.split('/blog/')[1];
      const page = blogPages.find(p => p.slug === slug);
      if (page) return "content";
    }
    if (path.startsWith('/alternatives/')) {
      const slug = path.split('/alternatives/')[1];
      const page = alternativesPages.find(p => p.slug === slug);
      if (page) return "content";
    }
    if (path === '/pricing') {
      return "pricing_page" as any;
    }
    if (path === '/admin/verify') {
      return "admin_verify" as any;
    }
    if (path === '/auth/gmail/callback') {
      return "gmail_callback" as any;
    }
    if (path === '/privacy' || path === '/privacy-policy' || path === '/data-deletion') {
      return "privacy" as any;
    }
    if (path === '/terms' || path === '/terms-of-service') {
      return "terms" as any;
    }

    if (path === '/' || path === '') {
      const saved = localStorage.getItem('lynx_view');
      // Only restore interactive views from local storage when visiting the root path.
      // Do not restore static pages like privacy or terms to the root URL.
      if (saved && ['landing', 'dashboard', 'campaign', 'analytics', 'jobs', 'auto_apply'].includes(saved)) {
        if (saved === 'dashboard') return 'campaign' as any;
        return saved as any;
      }
    }
    
    return "campaign";
  });
  const [campaignTheme, setCampaignTheme] = useState<"light" | "dark">("light");
  const [userPlan, setUserPlan] = useState<string>("free");
  const [planExpired, setPlanExpired] = useState(false);
  
  const wasLoggedIn = useRef(false);

  const navigate = (newView: any, path: string = '/') => {
    if (window.location.pathname !== path) {
      window.history.pushState({ view: newView }, '', path);
    }
    setView(newView);
  };

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (event.state && event.state.view) {
        setView(event.state.view);
      } else {
        const path = window.location.pathname;
        if (path === '/privacy' || path === '/privacy-policy' || path === '/data-deletion') {
          setView("privacy");
        } else if (path === '/terms' || path === '/terms-of-service') {
          setView("terms");
        } else if (path === '/about') {
          setView("about");
        } else if (path === '/refund') {
          setView("refund");
        } else if (path === '/contact') {
          setView("contact");
        } else if (path === '/pricing') {
          setView("pricing_page");
        } else if (path === '/') {
          setView(wasLoggedIn.current ? "campaign" : "landing");
        } else if (path === '/dashboard' || path === '/campaign') {
          setView("campaign");
        } else if (path === '/analytics') {
          setView("analytics");
        } else if (path === '/jobs') {
          setView("jobs");
        }
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    // Only save core app views to local storage
    if (view !== "content") {
      localStorage.setItem('lynx_view', view);
    }
  }, [view]);

  // Load the correct SEO page based on URL if we are in "content" view
  useEffect(() => {
    if (view === "content") {
      const path = window.location.pathname;
      if (path.startsWith('/blog/')) {
        const slug = path.split('/blog/')[1];
        setSeoPage(blogPages.find(p => p.slug === slug) || null);
      } else if (path.startsWith('/alternatives/')) {
        const slug = path.split('/alternatives/')[1];
        setSeoPage(alternativesPages.find(p => p.slug === slug) || null);
      }
    }
  }, [view]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const loggedIn = !!session?.user;
      wasLoggedIn.current = loggedIn;
      setUser(session?.user ?? null);
      if (!loggedIn) {
        // Prevent redirecting public routes to landing page on load
        const publicViews = ["content", "pricing_page", "privacy", "terms", "about", "refund", "contact", "landing", "gmail_callback"];
        if (!publicViews.includes(view)) {
          setView("landing");
        }
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null);
      if (event === 'SIGNED_OUT') {
        wasLoggedIn.current = false;
        setView("landing");
      } else if (event === 'SIGNED_IN') {
        if (!wasLoggedIn.current) {
          setView(prev => prev === "landing" ? "campaign" : prev);
          wasLoggedIn.current = true;

          // Process referral code if present
          const refCode = localStorage.getItem('lynx_referral_code');
          if (refCode && session?.user) {
            localStorage.removeItem('lynx_referral_code');
            supabase.rpc('process_referral', { ref_code: refCode }).then(({ error }) => {
              if (error) console.error('Referral processing error:', error);
            });
          }

          // Send welcome email ONLY on true first sign-up, not on every re-login.
          // Pre-check notification_log to avoid firing for existing users.
          if (session?.user) {
            supabase
              .from('notification_log')
              .select('id')
              .eq('user_id', session.user.id)
              .eq('notification_type', 'welcome')
              .limit(1)
              .maybeSingle()
              .then(({ data: alreadyWelcomed }) => {
                if (!alreadyWelcomed) {
                  supabase.functions.invoke('send-notification', {
                    body: { type: 'welcome', userId: session.user.id },
                  }).catch(e => console.error('Welcome email failed:', e));
                }
              });
          }
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Load user plan from profiles
  useEffect(() => {
    if (!user) { setUserPlan('free'); return; }
    async function loadPlan() {
      const { data } = await supabase
        .from('profiles')
        .select('subscription_tier, subscription_expires_at')
        .eq('id', user!.id)
        .maybeSingle();
      if (data) {
        const effective = getEffectivePlan(data.subscription_tier || 'free', data.subscription_expires_at);
        setUserPlan(effective);

        if (data.subscription_tier && data.subscription_tier !== 'free' && effective === 'free') {
          // The plan in DB is premium but effective plan is free -> it expired!
          setPlanExpired(true);
        }
      }
    }
    loadPlan();
  }, [user]);

  // Global Extension Listener (so AI doesn't hang if user leaves the AutoApply page)
  useEffect(() => {
    if (!user) return;
    
    let isProcessing = false;
    let lastRequestHash = '';
    
    const handleExtMessage = async (event: MessageEvent) => {
      if (event.source !== window) return;
      
      if (event.data?.type === 'LYNX_EXT_REQUEST_AI_FILL') {
        // Deduplicate: skip if we're already processing or if it's the same request
        const requestHash = JSON.stringify(event.data.payload.formSchema.map((f: any) => f.id).sort());
        if (isProcessing) {
          console.log("[Lynx App] Skipping duplicate AI fill request (already processing)");
          return;
        }
        if (requestHash === lastRequestHash) {
          console.log("[Lynx App] Skipping identical AI fill request");
          return;
        }
        
        isProcessing = true;
        lastRequestHash = requestHash;
        
        console.log("[Lynx App] Received AI fill request with", event.data.payload.formSchema.length, "fields");
        const { formSchema, profileData } = event.data.payload;
        try {
          const { data: resumeData, error } = await supabase
            .from('resume_data')
            .select('ai_summary, original_text')
            .eq('user_id', user.id)
            .maybeSingle();
          
          if (error) console.error("[Lynx App] Error fetching resume:", error);
          
          const origText = resumeData?.original_text || '';
          const aiSummary = resumeData?.ai_summary || '';
          // Pick the LONGER text — ai_summary is often richer than short original_text
          const resumeText = origText.length > aiSummary.length ? origText : (aiSummary || origText || "No resume found.");
          console.log("[Lynx App] Resume: original_text=" + origText.length + " chars, ai_summary=" + aiSummary.length + " chars. Using " + resumeText.length + " chars. Calling Mistral AI...");
          
          const { fillFormWithAI } = await import('./lib/mistralClient');
          const aiAnswers = await fillFormWithAI(resumeText, formSchema, profileData || {});
          console.log("[Lynx App] Mistral returned answers:", Object.keys(aiAnswers).length, "fields");
          
          window.postMessage({ type: 'LYNX_AI_FILL_RESPONSE', payload: { aiAnswers } }, '*');
          console.log("[Lynx App] Posted LYNX_AI_FILL_RESPONSE to window");
        } catch (err) {
          console.error("[Lynx App] Error calling Mistral for form filling", err);
          window.postMessage({ type: 'LYNX_AI_FILL_RESPONSE', payload: { aiAnswers: {} } }, '*');
        } finally {
          isProcessing = false;
        }
      }
    };
    
    window.addEventListener('message', handleExtMessage);
    return () => window.removeEventListener('message', handleExtMessage);
  }, [user]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="h-6 w-6 rounded-full border-2 border-white/20 border-t-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Navbar
        user={user}
        userPlan={userPlan}
        view={view as any}
        campaignTheme={campaignTheme}
        onToggleCampaignTheme={() => setCampaignTheme(prev => prev === "light" ? "dark" : "light")}
        onGoLanding={() => navigate("landing", "/")}
        onGoDashboard={() => navigate("campaign", "/dashboard")}
        onGoAnalytics={() => navigate("analytics", "/analytics")}
        onGoJobs={() => navigate("jobs", "/jobs")}
        // onGoAutoApply={() => navigate("auto_apply", "/auto-apply")} // AUTO APPLY HIDDEN
        onLogin={() => setShowAuth(true)}
        onLogout={handleLogout}
      />

      <Suspense fallback={
        <div className="min-h-[80vh] flex items-center justify-center">
          <div className="h-6 w-6 rounded-full border-2 border-white/20 border-t-white animate-spin" />
        </div>
      }>
        {view === "content" && seoPage ? (
          <ContentView 
            page={seoPage}
            user={user}
            onLogin={() => setShowAuth(true)}
            onLogout={handleLogout}
            onGoLanding={() => navigate("landing", "/")}
            onGoDashboard={() => navigate("campaign", "/dashboard")}
            onGoAnalytics={() => navigate("analytics", "/analytics")}
            onGoJobs={() => navigate("jobs", "/jobs")}
            onGoPrivacy={() => navigate("privacy", "/privacy")}
            onGoTerms={() => navigate("terms", "/terms")}
            onGoAbout={() => navigate("about", "/about")}
            onGoRefund={() => navigate("refund", "/refund")}
            onGoContact={() => navigate("contact", "/contact")}
          />
        ) : view === "pricing_page" ? (
          <>
            <Helmet>
              <title>Lynx Reach Pricing — Free, Starter, Pro | Job Search Assistant</title>
              <meta name="description" content="Start free with 50 email credits. Upgrade to Starter or Pro for unlimited AI email generation. Best value job search assistant for job seekers globally."/>
            </Helmet>
            <div className="pt-24 pb-12">
            </div>
          </>
        ) : view === "privacy" ? (
          <Privacy onGoBack={() => { window.history.back(); }} theme={campaignTheme} />
        ) : view === "terms" ? (
          <Terms onGoBack={() => { window.history.back(); }} theme={campaignTheme} />
        ) : view === "about" ? (
          <About onGoBack={() => { window.history.back(); }} theme={campaignTheme} />
        ) : view === "refund" ? (
          <Refund onGoBack={() => { window.history.back(); }} theme={campaignTheme} />
        ) : view === "contact" ? (
          <Contact onGoBack={() => { window.history.back(); }} theme={campaignTheme} />
        ) : view === "admin_verify" && user ? (
          <EmailVerifier onGoBack={() => navigate("campaign", "/dashboard")} />
        ) : !user || view === "landing" ? (
          <Landing 
            user={user} 
            userPlan={userPlan} 
            onLogin={() => setShowAuth(true)} 
            onGoToDashboard={() => navigate("campaign", "/dashboard")}
            onGoPrivacy={() => navigate("privacy", "/privacy")}
            onGoTerms={() => navigate("terms", "/terms")}
            onGoAbout={() => navigate("about", "/about")}
            onGoRefund={() => navigate("refund", "/refund")}
            onGoContact={() => navigate("contact", "/contact")}
          />
        ) : view === "campaign" || view === "dashboard" ? (
          <Campaign user={user} onGoDashboard={() => navigate("campaign", "/dashboard")} theme={campaignTheme} plan={userPlan} />
        ) : view === "analytics" ? (
          <Analytics user={user} onGoDashboard={() => navigate("campaign", "/dashboard")} theme={campaignTheme} plan={userPlan} />
        ) : view === "jobs" ? (
          <JobPortal user={user} plan={userPlan} />
        ) : view === "gmail_callback" ? (
          <GmailCallback />
        ) : (
          <Campaign user={user} onGoDashboard={() => navigate("campaign", "/dashboard")} theme={campaignTheme} plan={userPlan} />
        )}
      </Suspense>

      {/* Auth modal triggered from Navbar */}
      {showAuth && !user && (
        <AuthModal 
          onClose={() => setShowAuth(false)}
          onGoTerms={() => navigate("terms", "/terms")}
          onGoPrivacy={() => navigate("privacy", "/privacy")}
        />
      )}

      {/* Plan Expired Modal */}
      {planExpired && user && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setPlanExpired(false)}
          />
          <div className="relative w-full max-w-md rounded-3xl border border-rose-500/30 bg-zinc-900 p-8 shadow-2xl text-center">
            <button
              onClick={() => setPlanExpired(false)}
              className="absolute right-5 top-5 text-zinc-600 hover:text-zinc-300 transition-colors"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-rose-500/10 mb-6">
              <svg className="h-8 w-8 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Your Plan Has Expired</h2>
            <p className="text-zinc-400 mb-6">
              Your previous premium plan has reached its end date. You have been switched back to the Free tier with limited access.
            </p>
            <button
              onClick={() => {
                setPlanExpired(false);
              }}
              className="w-full bg-rose-600 hover:bg-rose-500 text-white font-bold py-3.5 px-6 rounded-xl transition-colors"
            >
              I Understand
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
